import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Clock, MapPin } from 'lucide-react';

export default function Footer() {
  return (
    <footer className="bg-brand-navy text-white pt-16 pb-8 border-t border-white/5 relative overflow-hidden">
      {/* Premium top gradient line border */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-blue/30 to-transparent" />
      
      {/* Background radial soft lights */}
      <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-[500px] h-[500px] bg-brand-blue/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 mb-12">
          
          {/* Column 1: Company Profile & Social Links */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left space-y-6">
            <Link to="/" className="flex items-center group justify-center lg:justify-start">
              <img 
                src="/assets/footer logo.png" 
                alt="Kiran Distributors" 
                className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
            
            <p className="text-sm text-brand-silver/80 font-poppins leading-relaxed max-w-sm">
              Serving healthcare institutions across Sangli district and Maharashtra with absolute reliability, premium medical furniture, modular OT setups, and genuine pharmaceuticals.
            </p>

            <div className="flex items-center gap-3 pt-2">
              <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-brand-silver/50">Follow Us:</span>
              <a 
                href="https://www.facebook.com/share/1EPFp5J8eq/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:border-brand-blue/50 hover:bg-brand-blue hover:text-white text-brand-silver flex items-center justify-center transition-all duration-300 shadow-md"
                aria-label="Facebook"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/kirandistributor?utm_source=qr&igsh=MTRyYmJhMmtiZ2d1ZQ%3D%3D" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:border-brand-blue/50 hover:bg-brand-blue hover:text-white text-brand-silver flex items-center justify-center transition-all duration-300 shadow-md"
                aria-label="Instagram"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Healthcare Services Links */}
          <div className="flex flex-col items-center lg:items-start">
            <h3 className="text-base font-bold font-montserrat text-white tracking-wide mb-6 pb-2 border-b-2 border-brand-blue/30 lg:border-b-0 lg:pb-0 lg:border-l-2 lg:border-brand-blue lg:pl-3 w-fit text-center lg:text-left">
              Our Services
            </h3>
            <ul className="space-y-3.5 text-sm font-poppins w-full flex flex-col items-center lg:items-start">
              {[
                'Medicines Distribution',
                'Surgical Goods Distribution',
                'Hospital Furniture Dealer',
                'Hospital Equipment Supplier',
                'Modular OT Setup Solutions',
                'ICU Setup Planning'
              ].map((service) => (
                <li key={service} className="w-fit">
                  <Link 
                    to="/services" 
                    className="group flex items-center gap-2 text-brand-silver/85 hover:text-brand-blue transition-all duration-300 text-center lg:text-left"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-blue opacity-0 group-hover:opacity-100 transition-all duration-300 scale-0 group-hover:scale-100 shrink-0" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">{service}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact Coordinates */}
          <div className="flex flex-col items-center lg:items-start w-full">
            <h3 className="text-base font-bold font-montserrat text-white tracking-wide mb-6 pb-2 border-b-2 border-brand-blue/30 lg:border-b-0 lg:pb-0 lg:border-l-2 lg:border-brand-blue lg:pl-3 w-fit text-center lg:text-left">
              Get in Touch
            </h3>
            
            <div className="space-y-5 font-poppins text-sm text-brand-silver/85 w-full flex flex-col items-center lg:items-start">
              
              {/* Head Office */}
              <div className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-3 max-w-xs lg:max-w-none group w-full justify-center lg:justify-start">
                <div className="p-2 rounded-lg bg-white/5 text-brand-blue shrink-0 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white shadow-md">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-xs text-white uppercase tracking-wider mb-1">Head Office</h4>
                  <p className="text-xs text-brand-silver/80 leading-normal">
                    Kiran Building, Gandhi Chowk,<br />Miraj – 416410
                  </p>
                </div>
              </div>

              {/* Branch Office */}
              <div className="flex flex-col items-center text-center lg:flex-row lg:items-start lg:text-left gap-3 max-w-xs lg:max-w-none group w-full justify-center lg:justify-start">
                <div className="p-2 rounded-lg bg-white/5 text-brand-blue shrink-0 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white shadow-md">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-xs text-white uppercase tracking-wider mb-1">Branch Office</h4>
                  <p className="text-xs text-brand-silver/80 leading-normal">
                    Shriram Akshay Building, G4,<br />Bapat Mala Road, Sangli
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex flex-col items-center text-center lg:flex-row lg:items-center lg:text-left gap-3 group w-full justify-center lg:justify-start">
                <div className="p-2 rounded-lg bg-white/5 text-brand-blue shrink-0 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white shadow-md">
                  <Phone className="w-4 h-4" />
                </div>
                <a href="tel:9225836006" className="text-xs hover:text-brand-blue transition-colors font-semibold tracking-wider">
                  9225836006
                </a>
              </div>

              {/* Email */}
              <div className="flex flex-col items-center text-center lg:flex-row lg:items-center lg:text-left gap-3 group w-full justify-center lg:justify-start max-w-full">
                <div className="p-2 rounded-lg bg-white/5 text-brand-blue shrink-0 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white shadow-md">
                  <Mail className="w-4 h-4" />
                </div>
                <a href="mailto:kirandistributors.miraj@gmail.com" className="text-xs hover:text-brand-blue transition-colors font-semibold break-all lg:break-normal">
                  kirandistributors.miraj@gmail.com
                </a>
              </div>

              {/* Timing */}
              <div className="flex flex-col items-center text-center lg:flex-row lg:items-center lg:text-left gap-3 group w-full justify-center lg:justify-start">
                <div className="p-2 rounded-lg bg-white/5 text-brand-blue shrink-0 flex items-center justify-center transition-all duration-300 group-hover:bg-brand-blue group-hover:text-white shadow-md">
                  <Clock className="w-4 h-4" />
                </div>
                <span className="text-xs text-brand-silver/70 font-medium">
                  10:00 AM – 7:00 PM
                </span>
              </div>

            </div>
          </div>

        </div>

        {/* Footer Bottom copyright area */}
        <div className="pt-8 mt-8 border-t border-white/[0.06] text-center text-xs text-brand-silver/50 font-poppins flex flex-col lg:flex-row justify-between items-center gap-4">
          <p className="text-brand-silver/60">© {new Date().getFullYear()} Kiran Distributors. All Rights Reserved.</p>
          <p className="text-brand-silver/60">
            Designed & Developed By{' '}
            <a 
              href="https://sahyadritech.in/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-bold text-brand-silver/80 hover:text-brand-blue transition-colors"
            >
              Sahyadri Tech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

